/**
 * Sign in.
 *
 * One primary action, one quiet way to each of the three places someone on this
 * screen might actually need: a forgotten password, a new account, or simply
 * carrying on without one.
 *
 * That last link is not a courtesy. Guest use is a first-class mode, and a sign
 * in screen with no way past it would turn every signed-out launch into a wall.
 */

import { useState, type FormEvent } from 'react';
import { Button } from '../design/Button';
import { TextField, PasswordField } from '../design/Field';
import { AuthLayout } from '../design/Feedback';
import { FailureSummary, useSubmission } from './formSupport';
import { useAuth } from './AuthProvider';
import type { Route } from './routes';

export interface SignInProps {
  navigate: (route: Route, options?: { replace?: boolean }) => void;
}

export function SignIn({ navigate }: SignInProps) {
  const { auth } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { busy, failure, submit } = useSubmission();

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const ok = await submit(async () => {
      const state = await auth.signIn(email.trim(), password);
      // Clear the password out of component state the moment it is no longer
      // needed, so it is not sitting in a React fibre for the rest of the session.
      setPassword('');
      if (state.status === 'verification-required') {
        navigate({ name: 'verify-email', key: null }, { replace: true });
      } else {
        // The app decides where a signed-in learner goes next; it may be the
        // local-work question rather than the writing surface.
        navigate({ name: 'app', destination: 'home' }, { replace: true });
      }
    });
    if (!ok) setPassword('');
  }

  return (
    <AuthLayout
      heading="Sign in"
      sub="Your work syncs to your account. Signed out, it stays on this iPad."
      footer={
        <>
          New to MusicMentor?{' '}
          <button
            type="button"
            className="mm-auth__link"
            onClick={() => navigate({ name: 'create-account' })}
          >
            Create account
          </button>
        </>
      }
    >
      <FailureSummary failure={failure} handledFields={['email', 'password']} />

      <form onSubmit={onSubmit} noValidate>
        <TextField
          label="Email"
          type="email"
          name="email"
          value={email}
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          required
          error={failure?.fields.email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <PasswordField
          label="Password"
          name="password"
          value={password}
          autoComplete="current-password"
          required
          error={failure?.fields.password}
          onChange={(event) => setPassword(event.target.value)}
        />

        <div className="mm-auth__actions">
          <Button type="submit" variant="primary" size="lg" block loading={busy}>
            Sign in
          </Button>
        </div>
      </form>

      <div className="mm-auth__aside">
        <button
          type="button"
          className="mm-auth__link"
          onClick={() => navigate({ name: 'forgot-password' })}
        >
          Forgot your password?
        </button>
        <button type="button" className="mm-auth__link" onClick={() => navigate({ name: 'app', destination: 'home' })}>
          Continue without an account
        </button>
      </div>
    </AuthLayout>
  );
}
